import OmniDialog from '@/components/OmniDialog'
import ViverseButton from '@/components/ViverseButton'
import ViverseRadio from '@/components/ViverseRadio'
import ViverseTextarea from '@/components/ViverseTextarea'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { WORLD_REPORT_REASON_VALUES } from '@/constants/report'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { reportWorldAction } from '@/server/lib/world'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

type WorldReportModalProps = {
  isDialogOpen: boolean
  onReportSuccess: () => void
  onClose: () => void
}

const DETAIL_MAX_LENGTH = 500

const formSchema = z.object({
  reason: z.enum(WORLD_REPORT_REASON_VALUES),
  detail: z.string().max(DETAIL_MAX_LENGTH),
})

const WorldReportModal = ({ isDialogOpen, onReportSuccess, onClose }: WorldReportModalProps) => {
  const t = useTranslations()
  const { store } = useConfigsContext()
  const {
    roomData: { id: roomId },
  } = useCheckEnterRoomContext()
  const htcAuthKey = store?.credentials?.htcToken

  const initialValues = useMemo(() => ({ reason: undefined, detail: '' }), [])
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  })

  const handleClose = () => {
    onClose()
    form.reset(initialValues)
  }

  const reportReasonOptions = useMemo(
    () =>
      WORLD_REPORT_REASON_VALUES.map((value) => ({
        value,
        label: t(`world-report-modal.reasons.${value}`),
      })),
    [t],
  )

  const submitReport = async (values: z.infer<typeof formSchema>) => {
    if (!htcAuthKey || !roomId) {
      console.log('Cannot submit report: missing authkey or roomId.')
      return
    }
    try {
      await reportWorldAction(roomId, htcAuthKey, values.reason, values.detail)
      onReportSuccess()
    } catch {
      console.error('Failed to report world')
    } finally {
      handleClose()
    }
  }

  const isSubmitDisabled = !form.formState.isValid

  return (
    <OmniDialog
      title={t('world-report-modal.title')}
      description={t('world-report-modal.description')}
      open={isDialogOpen}
      withCloseButton={false}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submitReport)} noValidate className="w-full space-y-4">
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ViverseRadio
                    onValueChange={field.onChange}
                    value={field.value}
                    options={reportReasonOptions}
                    optionRowClassName="py-0"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="detail"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ViverseTextarea
                    {...field}
                    containerClassName="border-none"
                    className="bg-vive-primary-70 text-vive-primary-30"
                    placeholder={t('world-report-modal.detail-placeholder')}
                    showMaxLength={false}
                    maxLength={DETAIL_MAX_LENGTH}
                    rows={4}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-full justify-center gap-4">
            <ViverseButton type="button" variant="secondary" onClick={handleClose}>
              {t('world-report-modal.cancel')}
            </ViverseButton>
            <ViverseButton
              type="submit"
              disabled={isSubmitDisabled}
              aria-disabled={isSubmitDisabled}
            >
              {t('world-report-modal.report')}
            </ViverseButton>
          </div>
        </form>
      </Form>
    </OmniDialog>
  )
}
export default WorldReportModal
